import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbar } from '@angular/material/toolbar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { isDuplicateTemplateName, trackerConfigSettingsEntries } from 'src/app/classes/shared/tracker-config-display.util';
import { Pop } from 'src/app/classes/shared/pop';
import { Tracker } from 'src/app/interfaces/api/tracker';
import { TrackerConfig, defaultConfig } from 'src/app/interfaces/api/trackerConfig';
import { TrackerService } from 'src/app/services/api/trackers/tracker.service';
import { ButtonComponent, ConfirmComponent } from 'ui-library';
import { TrackerConfigFormComponent } from 'src/app/components/project-settings/resources/tracker-config-form/tracker-config-form.component';

export interface TrackerConfigDetailDialogData {
  // null means "create a new template" - opens straight into edit mode with an empty draft.
  // Ignored when `queue` is set.
  config: TrackerConfig | null;
  trackers: Tracker[];
  // Every existing template (all types) - for the duplicate-name check in save() below. Kept
  // live by the caller (tracker-configs.component.ts already keeps its own `configs` live via
  // the real-time socket subscription), so this stays accurate without a fetch of its own here.
  configs: TrackerConfig[];
  // Skips the read-only detail step and goes straight to editing.
  startInEdit?: boolean;
  // Sequential bulk edit ("Modifier la sélection", tracker-configs.component.ts's
  // bulkEditSequential()) - every template after the first is loaded into this SAME dialog
  // instance instead of closing and reopening a new one for each, which used to visibly flash
  // the page underneath between templates.
  queue?: TrackerConfig[];
  usedBy?: (config: TrackerConfig) => Tracker[];
}

// Detail/edit/create for a TrackerConfig template - opened from tracker-configs.component.ts
// (the "Devices > engrenage" manager, all device types included) for one row/card at a time, or
// as a sequential queue for a bulk edit. Closes with 'saved' | 'deleted' | undefined (cancelled)
// so the parent knows whether to refresh its list - always refresh for a queue, since earlier
// items may already have been saved even if the dialog was closed early.
@Component({
  selector: 'app-tracker-config-detail-dialog',
  imports: [
    CommonModule,
    TranslateModule,
    MatDialogModule,
    MatIconModule,
    MatToolbar,
    ButtonComponent,
    TrackerConfigFormComponent,
  ],
  standalone: true,
  templateUrl: './tracker-config-detail-dialog.component.html',
  styleUrls: ['./tracker-config-detail-dialog.component.scss']
})
export class TrackerConfigDetailDialogComponent {
  private readonly trackerService: TrackerService = inject(TrackerService);
  private readonly pop: Pop = inject(Pop);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly data: TrackerConfigDetailDialogData = inject(MAT_DIALOG_DATA);
  readonly dialogRef: MatDialogRef<TrackerConfigDetailDialogComponent, 'saved' | 'deleted' | undefined> =
    inject(MatDialogRef<TrackerConfigDetailDialogComponent, 'saved' | 'deleted' | undefined>);

  private readonly queue: TrackerConfig[] = this.data.queue ? [...this.data.queue] : [];
  // Bound to the embedded form's own [existingConfigs] (see tracker-config-form.component.ts's
  // duplicate-name check) - public since a private field wouldn't be reachable from the template.
  readonly configs: TrackerConfig[] = this.data.configs;
  private readonly usedByFn: (config: TrackerConfig) => Tracker[] = this.data.usedBy ?? (() => this.data.trackers || []);

  readonly isSequential: boolean = !!this.data.queue?.length;
  readonly queueTotal: number = this.data.queue?.length ?? 0;
  queuePosition: number = 1;

  config: TrackerConfig | null = null;
  isNew: boolean = false;
  trackers: Tracker[] = [];
  mode: 'view' | 'edit' = 'view';
  configDraft: TrackerConfig = { ...defaultConfig };

  constructor() {
    this.loadCurrent(this.isSequential ? this.queue.shift()! : this.data.config);
  }

  private loadCurrent(config: TrackerConfig | null) {
    this.config = config;
    this.isNew = !config;
    this.trackers = config ? this.usedByFn(config) : (this.data.trackers || []);
    this.configDraft = config ? { ...config, settings: { ...config.settings } } : { ...defaultConfig };
    this.mode = config && !this.data.startInEdit && !this.isSequential ? 'view' : 'edit';
  }

  // Moves the queue forward in place (no close/reopen of the dialog, which visibly flashed the
  // page underneath between templates) - closes only once the queue is exhausted.
  private advanceOrClose(result: 'saved' | 'deleted') {
    if (this.queue.length) {
      this.queuePosition++;
      this.loadCurrent(this.queue.shift()!);
    } else {
      this.dialogRef.close(result);
    }
  }

  get settingsEntries(): { key: string; value: string }[] {
    return trackerConfigSettingsEntries(this.config?.settings, this.translate, this.config?.type);
  }

  startEdit() {
    this.mode = 'edit';
  }

  cancelEdit() {
    if (this.isSequential) {
      this.advanceOrClose('saved');
      return;
    }

    if (this.isNew) {
      this.dialogRef.close(undefined);
      return;
    }

    this.configDraft = { ...this.config!, settings: { ...this.config!.settings } };
    this.mode = 'view';
  }

  save() {
    this.trackerService.setConfig(this.configDraft as any).subscribe({
      next: () => {
        this.pop.toast(this.translate.instant('resources.template_saved'), 'success', 4000, 'save');
        this.advanceOrClose('saved');
      },
      error: (error: any) => {
        this.pop.error(error?.error?.error ?? this.translate.instant('trackers.error.config_save'));
      }
    });
  }

  delete() {
    if (!this.config) return;

    this.dialog.open(ConfirmComponent, {
      width: '500px',
      data: {
        icon: 'delete',
        title: this.translate.instant('menu.devices.configs.delete_confirm_title'),
        content: this.translate.instant('menu.devices.configs.delete_confirm_content', { name: this.config.name }),
        okIcon: 'delete_forever',
        okColor: 'danger',
        okText: 'form.delete',
        cancelText: 'form.cancel',
      }
    }).afterClosed().subscribe(result => {
      if (!result?.ok) return;

      this.trackerService.deleteConfig(this.config!.id).subscribe(() => {
        this.pop.toast(this.translate.instant('units.device.config_removed'), 'success', 4000, 'delete');
        this.advanceOrClose('deleted');
      });
    });
  }

  close() {
    // A queue closed early may already have saved/deleted earlier items - the parent always
    // refreshes on any non-undefined result, so this covers that instead of losing track of it.
    this.dialogRef.close(this.isSequential ? 'saved' : undefined);
  }
}
